import React, { useState, useEffect, useMemo } from 'react';
import { Button, Modal, DatePicker, Space, notification, Alert, Checkbox, InputNumber } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { usePointOfSaleService } from '../../../services/pointofsale/PointOfSaleServiceProvider';
import { useMemberService } from '../../../services/members/MemberServiceProvider';
import { useMember } from '../../../common/member/MemberContext';
import dayjs, { Dayjs } from 'dayjs';
import { MemberProfile } from '../../../model/members/MemberProfile';
import { PointOfSaleTransaction } from '../../../model/pointofsale/PointOfSaleTransaction';
import { UpdatePaymentTransactionRequest } from '../../../model/pointofsale/UpdatePaymentTransactionRequest';
import { PointOfSaleItem } from '../../../model/pointofsale/PointOfSaleItem';
import { MembershipCategory } from '../../../model/members/MembershipCategory';
import MembershipCategorySelector from '../MembershipCategorySelector';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useReceiptPrinterService } from '../../../services/printing/ReceiptPrinterServiceProvider';
import { calculateEndDate } from '../../../utils/membershipDateUtils';
import { toDateOnly } from '../../../utils/DateOnly';
import { useCustomAmount } from '../../../hooks/useCustomAmount';
import { createTransactionItem, createTransactionRequest, processReceipt } from '../../../utils/membershipTransactionUtils';
import { CustomAmountInput } from './shared/CustomAmountInput';
import { TransactionSummary } from './shared/TransactionSummary';
import { StatusSummary } from './shared/StatusSummary';
import { useFormatCurrency, useCurrencySymbol } from '../../../hooks/useFormatters';
import { useClientConfig } from '../../../common/client/ClientConfigProvider';
import { calculateProRata } from '../../../utils/billingCycle';
import { parseJoiningFee } from '../joiningFee';

interface CreateJoiningFeeTransactionProps {
  member: MemberProfile;
  pointOfSaleTransactions: PointOfSaleTransaction[];
  membershipCategories: MembershipCategory[];
  onTransactionUpdated?: () => void;
  onMemberUpdated?: () => void;
  hideButton?: boolean;
  parentModalVisible?: boolean;
  onParentCancel?: () => void;
}

const CreateJoiningFeeTransaction: React.FC<CreateJoiningFeeTransactionProps> = ({
  member,
  pointOfSaleTransactions,
  membershipCategories,
  onTransactionUpdated,
  onMemberUpdated,
  hideButton = false,
  parentModalVisible = false,
  onParentCancel
}) => {
  const [isModalVisible, setIsModalVisible] = useState(!hideButton ? false : parentModalVisible);
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs | null>(dayjs());
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs | null>(null);
  const [useCustomEndDate, setUseCustomEndDate] = useState(false);
  const [selectedMembershipCategory, setSelectedMembershipCategory] = useState<MembershipCategory | null>(member.membershipCategory);
  const [includeJoiningFee, setIncludeJoiningFee] = useState(true);
  const [joiningFeeAmount, setJoiningFeeAmount] = useState<number | null>(null);
  const [proRataAmount, setProRataAmount] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const customAmountHook = useCustomAmount(proRataAmount);
  const [memberJoiningFeePosItem, setMemberJoiningFeePosItem] = useState<PointOfSaleItem | null>(null);
  const [pendingTransaction, setPendingTransaction] = useState<PointOfSaleTransaction | null>(null);
  const receiptPrinterService = useReceiptPrinterService();
  const pointOfSaleService = usePointOfSaleService();
  const memberService = useMemberService();
  const { isAdmin } = useMember();
  const formatCurrency = useFormatCurrency();
  const currencySymbol = useCurrencySymbol();
  const { billing, fees } = useClientConfig();

  // Joining fee (amount / POS item / label) is client-configurable via the
  // fees.joining setting; defaults reproduce the previous hardcoded £25 behaviour.
  const joiningFee = useMemo(() => parseJoiningFee(fees?.joiningJson), [fees?.joiningJson]);

  // Seed the editable joining-fee amount and the include/exclude default from
  // the client's configured joining fee once config is available (and whenever
  // it changes). Clients who disable the fee in Settings get it unticked by
  // default here; the operator can still toggle it on per transaction.
  useEffect(() => {
    setJoiningFeeAmount(joiningFee.amount);
    setIncludeJoiningFee(joiningFee.enabled);
  }, [joiningFee.amount, joiningFee.enabled]);

  // Pro-rate the first monthly period using the client's billing cycle
  // (anchor day + cut-off). Mirrors the backend BillingCycleService.
  const calculateProRataAmount = (price: number, startDate: Dayjs | null = null) =>
    calculateProRata(price, startDate ?? dayjs(), billing).amount;

  // Find pending transaction with Initial Member Joining Fee
  useEffect(() => {
    const findPendingTransaction = () => {
      return pointOfSaleTransactions.find(transaction =>
        transaction.status === 'Pending' &&
        transaction.items.some(item =>
          item.item.name === joiningFee.posItemName
        )
      ) || null;
    };

    setPendingTransaction(findPendingTransaction());
  }, [pointOfSaleTransactions, joiningFee.posItemName]);

  // Fetch the Initial Member Joining Fee POS item
  useEffect(() => {
    const fetchMemberJoiningFeePosItem = async () => {
      try {
        const item = await pointOfSaleService.getPosItem(joiningFee.posItemName);
        setMemberJoiningFeePosItem(item);
      } catch (error) {
        console.error('Error fetching member joining fee POS item:', error);
      }
    };
    fetchMemberJoiningFeePosItem();
  }, [pointOfSaleService, joiningFee.posItemName]);

  // Calculate end date when start date or membership category changes
  useEffect(() => {
    if (selectedStartDate && selectedMembershipCategory && !useCustomEndDate) {
      const calculatedEndDate = calculateEndDate(selectedStartDate, selectedMembershipCategory, billing);
      setSelectedEndDate(calculatedEndDate);
    }
  }, [selectedStartDate, selectedMembershipCategory, useCustomEndDate, billing]);

  // Calculate amount when start date, membership category, or joining fee changes
  useEffect(() => {
    if (selectedStartDate && selectedMembershipCategory) {
      let amount;
      
      // Only calculate pro-rata for Monthly payment frequency
      if (selectedMembershipCategory.paymentFrequency === 'Monthly') {
        amount = calculateProRataAmount(selectedMembershipCategory.amount, selectedStartDate);
      } else {
        // For non-Monthly frequencies, use the full membership category amount
        amount = selectedMembershipCategory.amount;
      }
      
      if (includeJoiningFee) {
        amount += joiningFeeAmount ?? 0;
      }
      setProRataAmount(amount);
      // Reset custom amount when calculated amount changes
      if (!customAmountHook.useCustomAmount) {
        customAmountHook.setCustomAmount(amount);
      }
    }
    // calculateProRataAmount is redefined each render; excluded to keep this
    // effect keyed on the start date / category / joining-fee inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStartDate, selectedMembershipCategory, includeJoiningFee, joiningFeeAmount, customAmountHook]);

    // Update modal visibility when parentModalVisible changes (for parent control)
    useEffect(() => {
      if (hideButton) {
        setIsModalVisible(parentModalVisible);
      } else {
        setIsModalVisible(true);
      }
    }, [parentModalVisible, hideButton]);


  const handleStartDateChange = (date: Dayjs | null) => {
    setSelectedStartDate(date);
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    setSelectedEndDate(date);
  };

  const handleMembershipCategoryChange = (category: MembershipCategory | null) => {
    setSelectedMembershipCategory(category);
  };

  const handleJoiningFeeChange = (e: CheckboxChangeEvent) => {
    setIncludeJoiningFee(e.target.checked);
  };

  const handleJoiningFeeAmountChange = (value: number | null) => {
    setJoiningFeeAmount(value ?? 0);
  };

  const handleCustomEndDateChange = (e: CheckboxChangeEvent) => {
    setUseCustomEndDate(e.target.checked);
    if (!e.target.checked) {
      // Reset to calculated end date
      if (selectedStartDate && selectedMembershipCategory) {
        const calculatedEndDate = calculateEndDate(selectedStartDate, selectedMembershipCategory, billing);
        setSelectedEndDate(calculatedEndDate);
      }
    }
  };

  const getFinalAmount = () => {
    return customAmountHook.getFinalAmount(proRataAmount);
  };

  if (!isAdmin) {
    return null;
  }

  // Check if there's a pending transaction to process
  const hasPendingTransaction = pendingTransaction !== null;


  const handleProcessTransaction = async () => {
    await processTransaction("Completed", "Membership transaction created successfully");
  };


  const handleMarkAsUnpaid = async () => {
    await processTransaction("Unpaid", "Membership transaction marked as unpaid successfully");
  };

  const processTransaction = async (transactionStatus: string, successMessage: string) => {
    if (!selectedStartDate || !selectedEndDate || !memberJoiningFeePosItem || !selectedMembershipCategory) {
      notification.error({
        message: 'Error',
        description: 'Please select a start date, end date, membership category and ensure joining fee item is available'
      });
      return;
    }

    setIsProcessing(true);
    try {
      // Check if membership category, start date, or end date has changed and save member if needed
      const hasChanges = selectedMembershipCategory.name !== member.membershipCategory.name || 
                        (member.validFrom && !selectedStartDate.isSame(dayjs(member.validFrom), 'day')) ||
                        (!member.validFrom && selectedStartDate) ||
                        (member.validTo && !selectedEndDate.isSame(dayjs(member.validTo), 'day')) ||
                        (!member.validTo && selectedEndDate);
      
      if (hasChanges && selectedMembershipCategory) {
        const validToStr = toDateOnly(selectedEndDate);
        const validFromStr = toDateOnly(selectedStartDate);
        
        // For joining fee, we also update validFrom
        try {
          notification.info({
            message: 'Updating Member',
            description: 'Updating member details with new start date, end date and category...'
          });
          
          await memberService.update({
            ...member,
            membershipCategory: selectedMembershipCategory,
            validFrom: validFromStr,
            validTo: validToStr
          });
          
          notification.success({
            message: 'Member Updated',
            description: 'Member details updated successfully'
          });
          
          if (onMemberUpdated) {
            onMemberUpdated();
          }
        } catch (error) {
          console.error('Error updating member:', error);
          notification.warning({
            message: 'Warning',
            description: 'Transaction processed but failed to update member details'
          });
        }
      }

      const finalAmount = getFinalAmount();
      if (!finalAmount) {
        notification.error({
          message: 'Error',
          description: 'No amount specified for transaction'
        });
        return;
      }

      if (pendingTransaction) {
        // Update existing pending transaction
        const transactionItem = createTransactionItem(memberJoiningFeePosItem, finalAmount);
        const updateRequest: UpdatePaymentTransactionRequest = {
          transactionId: pendingTransaction.transactionId,
          status: transactionStatus,
          amount: finalAmount,
          items: [transactionItem],
          bookingSlots: pendingTransaction.bookingSlots
        };

        const transaction = await pointOfSaleService.updateTransaction(updateRequest);
        await processReceipt(transaction, receiptPrinterService, transactionStatus, member.name);
        
        notification.success({
          message: 'Success',
          description: successMessage
        });
      } else {
        // Create new transaction
        const transactionItem = createTransactionItem(memberJoiningFeePosItem, finalAmount);
        const createRequest = createTransactionRequest(member.membershipId, [transactionItem], finalAmount, transactionStatus);
        const transaction = await pointOfSaleService.makePayment(createRequest);
        await processReceipt(transaction, receiptPrinterService, transactionStatus, member.name);
        
        notification.success({
          message: 'Success',
          description: successMessage
        });
      }
      
      setIsModalVisible(false);
      if (onTransactionUpdated) {
        onTransactionUpdated();
      }
    } catch (error) {
      console.error('Error processing membership transaction:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to process membership transaction'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
    setSelectedStartDate(dayjs());
    setSelectedMembershipCategory(member.membershipCategory);
    setIncludeJoiningFee(joiningFee.enabled);
    setJoiningFeeAmount(joiningFee.amount);
    customAmountHook.reset();
    setUseCustomEndDate(false);

    // Set initial end date to member's existing validTo date, or calculate if none exists
    if (member.validTo) {
      setSelectedEndDate(dayjs(member.validTo));
    } else if (member.membershipCategory) {
      const calculatedEndDate = calculateEndDate(dayjs(), member.membershipCategory, billing);
      setSelectedEndDate(calculatedEndDate);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedStartDate(dayjs());
    setSelectedMembershipCategory(member.membershipCategory);
    setIncludeJoiningFee(joiningFee.enabled);
    setJoiningFeeAmount(joiningFee.amount);
    customAmountHook.reset();
    setProRataAmount(null);
    setUseCustomEndDate(false);
    setSelectedEndDate(null);
  };



  const formContent = (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            message="Joining Fee Transaction Processing"
            description={
              hasPendingTransaction 
                ? "Update the existing pending transaction with new start date, end date and pro-rata amount"
                : "Create a new transaction for the Initial Member Joining Fee"
            }
            type="info"
            showIcon
          />

          <div style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>Membership Category: *</div>
            <MembershipCategorySelector
              membershipCategories={membershipCategories}
              onChange={handleMembershipCategoryChange}
              value={selectedMembershipCategory?.name}
            />
            {!selectedMembershipCategory && (
              <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                Please select a membership category to proceed
              </div>
              )}
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>Membership Start Date: *</div>
            <DatePicker
              format="DD/MM/YYYY"
              value={selectedStartDate}
              onChange={handleStartDateChange}
              style={{ width: '100%' }}              
              disabled={isProcessing}
              placeholder="Select start date"
            />
            {!selectedStartDate && (
              <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                Please select a start date to proceed
              </div>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>Membership End Date: *</div>
            <div style={{ marginBottom: '8px' }}>
              <Checkbox 
                checked={useCustomEndDate}
                onChange={handleCustomEndDateChange}
                disabled={isProcessing}
              >
                Use Custom End Date
              </Checkbox>
            </div>
            <DatePicker
              format="DD/MM/YYYY"
              value={selectedEndDate}
              onChange={handleEndDateChange}
              style={{ width: '100%' }}              
              disabled={isProcessing || !useCustomEndDate}
              placeholder="Select end date"
            />
            {!selectedEndDate && (
              <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                Please select an end date to proceed
              </div>
            )}
            {selectedMembershipCategory && selectedStartDate && !useCustomEndDate && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Auto-calculated: {selectedMembershipCategory.paymentFrequency.toLowerCase() === 'monthly' ? 'Pro-rata period end (billing cycle)' : 'One year from start date'}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <Checkbox
              checked={includeJoiningFee}
              onChange={handleJoiningFeeChange}
              disabled={isProcessing}
            >
              {joiningFee.label}
            </Checkbox>
            {includeJoiningFee && (
              <div style={{ marginTop: '8px' }}>
                <InputNumber
                  inputMode="decimal"
                  value={joiningFeeAmount}
                  onChange={handleJoiningFeeAmountChange}
                  min={0}
                  step={0.01}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="Enter joining fee amount"
                  disabled={isProcessing}
                  prefix={currencySymbol}
                />
              </div>
            )}
          </div>

          <CustomAmountInput
            useCustomAmount={customAmountHook.useCustomAmount}
            customAmount={customAmountHook.customAmount}
            defaultAmount={proRataAmount}
            onUseCustomAmountChange={customAmountHook.handleUseCustomAmountChange}
            onCustomAmountChange={customAmountHook.handleCustomAmountChange}
            disabled={isProcessing}
            showDefaultAmount={false}
          />

          {proRataAmount !== null && selectedMembershipCategory && (
            <TransactionSummary
              amount={getFinalAmount()}
              useCustomAmount={customAmountHook.useCustomAmount}
              defaultAmount={proRataAmount}
              membershipCategory={selectedMembershipCategory}
              customFields={
                <>
                  <div><strong>Base Amount:</strong> {formatCurrency(selectedMembershipCategory.amount)}</div>
                  <div><strong>Start Date:</strong> {selectedStartDate?.format('DD/MM/YYYY')}</div>
                  <div><strong>End Date:</strong> {selectedEndDate?.format('DD/MM/YYYY')}</div>
                  {includeJoiningFee && <div><strong>Joining Fee:</strong> {formatCurrency(joiningFeeAmount ?? 0)}</div>}
                  {customAmountHook.useCustomAmount && <div style={{ color: '#fa8c16' }}><strong>Amount Override:</strong> Active</div>}
                  {useCustomEndDate && <div style={{ color: '#fa8c16' }}><strong>End Date Override:</strong> Active</div>}
                </>
              }
            />
          )}

          {hasPendingTransaction && (
            <Alert
              message="Existing Transaction"
              description={`Transaction ID: ${pendingTransaction?.transactionId} - Status: ${pendingTransaction?.status}`}
              type="warning"
              showIcon
            />
          )}

          {/* Status Summary */}
          <StatusSummary
            items={[
              {
                label: 'Membership Category',
                isValid: !!selectedMembershipCategory,
                value: selectedMembershipCategory ? 'Selected' : 'Required'
              },
              {
                label: 'Start Date',
                isValid: !!selectedStartDate,
                value: selectedStartDate ? 'Selected' : 'Required'
              },
              {
                label: 'End Date',
                isValid: !!selectedEndDate,
                value: selectedEndDate ? 'Selected' : 'Required'
              },
              {
                label: 'Amount',
                isValid: !!getFinalAmount(),
                value: getFinalAmount() ? formatCurrency(getFinalAmount() ?? 0) : 'Calculating...',
                warning: customAmountHook.useCustomAmount ? '(Custom)' : undefined
              }
            ]}
          />
        {hideButton && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #d9d9d9' }}>
            <Button onClick={onParentCancel || handleCancel} disabled={isProcessing}>
              Cancel
            </Button>
            <Button
              type="primary"
              danger
              onClick={handleMarkAsUnpaid}
              loading={isProcessing}
              disabled={isProcessing || !selectedStartDate || !selectedEndDate || !proRataAmount}
              style={{ backgroundColor: '#ff4d4f' }}
            >
              Mark as Unpaid
            </Button>
            <Button
              type="primary"
              onClick={handleProcessTransaction}
              loading={isProcessing}
              disabled={isProcessing || !selectedStartDate || !selectedEndDate || !proRataAmount}
              style={{ backgroundColor: '#1890ff' }}
            >
              Process Transaction
            </Button>
          </div>
        )}
        </Space>
  );

  const modalContent = (
    <Modal
      title="Create Joining Fee Transaction"
      open={isModalVisible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isProcessing}>
          Cancel
        </Button>,
        <Button
          key="markAsUnpaid"
          type="primary"
          danger
          onClick={handleMarkAsUnpaid}
          loading={isProcessing}
          disabled={isProcessing || !selectedStartDate || !selectedEndDate || !proRataAmount}
          style={{ backgroundColor: '#ff4d4f' }}
        >
          Mark as Unpaid
        </Button>,
        <Button
          key="processTransaction"
          type="primary"
          onClick={handleProcessTransaction}
          loading={isProcessing}
          disabled={isProcessing || !selectedStartDate || !selectedEndDate || !proRataAmount}
          style={{ backgroundColor: '#1890ff' }}
        >
          Process Transaction
        </Button>
      ]}
      width={600}
    >
      {formContent}
    </Modal>
  );

  if (hideButton) {
    return formContent;
  }

  return (
    <>
      <Button
        type="primary"
        icon={<DollarOutlined />}
        onClick={showModal}
        style={{ backgroundColor: '#1890ff' }}
      >
        {hasPendingTransaction ? 'Update Joining Fee Transaction' : 'Create Joining Fee Transaction'}
      </Button>
      {modalContent}
    </>
  );
};

export default CreateJoiningFeeTransaction;
